class FocusStickyPurchase extends HTMLElement {
  connectedCallback() {
    this.productRoot = this.closest('[data-product-url]');
    this.originalButton = this.productRoot?.querySelector('[data-product-atc]');
    this.selection = this.productRoot?.querySelector('[data-variant-selection]');
    this.buyButton = this.querySelector('[data-sticky-buy]');
    this.prices = JSON.parse(this.querySelector('[data-sticky-variants]').textContent);
    if (!this.originalButton || !this.selection) return;
    this.onVariantChange = (event) => this.updateVariant(event.detail.variant);
    this.onBuy = () => {
      if (this.variant && this.variant.available && !this.originalButton.disabled) {
        this.originalButton.click();
      } else {
        this.originalButton.scrollIntoView({block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        this.productRoot.querySelector('[data-variant-option-value]:not(:disabled)')?.focus({preventScroll: true});
      }
    };
    this.selection.addEventListener('variant-change', this.onVariantChange);
    this.buyButton.addEventListener('click', this.onBuy);
    this.updateVisibility = () => {
      const bounds = this.originalButton.getBoundingClientRect();
      this.hidden = bounds.bottom > 0;
      document.body.classList.toggle('focus-sticky-active', !this.hidden);
    };
    this.onScroll = () => {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    };
    window.addEventListener('scroll', this.onScroll, {passive: true});
    window.addEventListener('resize', this.onScroll);
    this.observer = new IntersectionObserver(this.updateVisibility);
    this.observer.observe(this.originalButton);
    customElements.whenDefined(this.selection.localName).then(() => {
      if (this.isConnected && typeof this.selection.getVariant === 'function') {
        this.selection.getVariant().then((variant) => { if (this.isConnected) this.updateVariant(variant); });
      }
    });
  }

  updateVariant(variant) {
    this.variant = variant;
    this.buyButton.disabled = Boolean(variant && !variant.available);
    this.buyButton.textContent = !variant ? this.dataset.chooseLabel : variant.available ? this.dataset.addLabel : this.dataset.unavailableLabel;
    const selected = variant && this.prices[variant.id];
    this.querySelector('[data-sticky-variant]').textContent = selected && selected.title !== 'Default Title' ? selected.title : '';
    if (selected) this.querySelector('[data-sticky-price]').textContent = selected.price;
  }

  disconnectedCallback() {
    this.selection?.removeEventListener('variant-change', this.onVariantChange);
    this.buyButton?.removeEventListener('click', this.onBuy);
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    cancelAnimationFrame(this.visibilityFrame);
    document.body.classList.remove('focus-sticky-active');
  }
}

if (!customElements.get('focus-sticky-purchase')) customElements.define('focus-sticky-purchase', FocusStickyPurchase);

// Native dialog provides keyboard focus containment and Escape dismissal.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-focus-size-help]');
  if (trigger) {
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel || panel.open) return;
    panel.showModal();
    document.documentElement.classList.add('focus-size-modal-open');
    panel.addEventListener('close', () => {
      document.documentElement.classList.remove('focus-size-modal-open');
      trigger.focus({preventScroll: true});
    }, {once: true});
  }
  if (event.target.closest('[data-focus-size-chat]')) event.target.closest('dialog.focus-size-modal')?.close();
  const panel = event.target;
  if (panel.matches?.('dialog.focus-size-modal')) {
    const rect = panel.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) panel.close();
  }
});

class FocusShippingEstimate extends HTMLElement {
  connectedCallback() {
    if (window.location.hash === `#${this.id}`) {
      const panel = this.closest("details");
      if (panel) panel.open = true;
      requestAnimationFrame(() => this.scrollIntoView({block: "start"}));
    }
    this.form = this.querySelector('form');
    this.country = this.form.elements.country;
    this.province = this.form.elements.province;
    this.result = this.querySelector('[role="status"]');
    this.button = this.form.querySelector('[type="submit"]');
    this.country.value = this.country.dataset.default;
    this.onCountryChange = () => {
      const provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]');
      this.province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
      this.querySelector('[data-focus-province]').hidden = !provinces.length;
    };
    this.onChange = () => { this.result.textContent = ''; };
    this.onSubmit = (event) => { event.preventDefault(); this.calculate(); };
    this.country.addEventListener('change', this.onCountryChange);
    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('submit', this.onSubmit);
    this.onCountryChange();
  }

  async calculate() {
    if (this.button.disabled) return;
    const buttonLabel = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = this.dataset.loading;
    const fields = [...this.form.querySelectorAll('input, select')];
    fields.forEach((field) => { field.disabled = true; });
    this.result.textContent = '';
    this.controller = new AbortController();
    const timeout = setTimeout(() => this.controller.abort(), 20000);
    const params = new URLSearchParams();
    ['country', 'province', 'zip'].forEach((key) => params.set(`shipping_address[${key}]`, this.form.elements[key].value.trim()));
    const request = async (endpoint, method = 'GET') => {
      const response = await fetch(`${this.dataset.cartUrl}/${endpoint}.json?${params}`, {method, signal: this.controller.signal, credentials: 'same-origin'});
      if (!response.ok) throw new Error('Shipping request failed');
      return response.json();
    };
    try {
      await request('prepare_shipping_rates', 'POST');
      let data = null;
      for (let attempt = 0; attempt < 12 && !data; attempt++) {
        if (attempt) await new Promise((resolve) => setTimeout(resolve, 800));
        if (!this.isConnected || this.controller.signal.aborted) throw new Error('Cancelled');
        data = await request('async_shipping_rates');
      }
      if (!data || !Array.isArray(data.shipping_rates)) throw new Error('No response');
      const rates = data.shipping_rates;
      if (!rates.length) this.result.textContent = this.dataset.empty;
      const locale = this.dataset.currency === 'ARS' && this.dataset.locale.startsWith('es') ? 'es-AR' : this.dataset.locale;
      const formatter = new Intl.NumberFormat(locale, {style: 'currency', currency: this.dataset.currency, currencyDisplay: 'narrowSymbol'});
      rates.forEach((rate) => {
        const amount = Number(rate.price);
        if (!Number.isFinite(amount)) throw new Error('Invalid rate');
        // A misnamed paid rate must never be presented as free delivery.
        const name = amount > 0 && /gratis|gratuit|free/i.test(rate.name) ? this.dataset.rateLabel : rate.name;
        const row = document.createElement('p');
        row.textContent = `${name}: ${formatter.format(amount)}`;
        this.result.append(row);
      });
    } catch (error) {
      if (this.isConnected) this.result.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      this.button.disabled = false;
      this.button.textContent = buttonLabel;
      fields.forEach((field) => { field.disabled = false; });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.country?.removeEventListener('change', this.onCountryChange);
    this.form?.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('submit', this.onSubmit);
  }
}
if (!customElements.get('focus-shipping-estimate')) customElements.define('focus-shipping-estimate', FocusShippingEstimate);


document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-focus-size-chat]');
  if (!trigger) return;
  const chat = document.querySelector('shopify-chat');
  const status = trigger.parentElement.querySelector('.focus-chat-status');
  if (!chat) return;
  event.preventDefault();
  let copied = false;
  try {
    await navigator.clipboard.writeText(trigger.dataset.question);
    copied = true;
  } catch {}
  try {
    if (typeof chat.show === 'function') chat.show();
    else chat.setAttribute('open', '');
    status.textContent = copied ? trigger.dataset.copied : trigger.dataset.opened;
    status.hidden = false;
  } catch {
    status.textContent = trigger.dataset.unavailable;
    status.hidden = false;
    window.location.assign(trigger.href);
  }
});

class FocusCategoryCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-category-track]');
    this.items = [...this.querySelectorAll('.collection-categories__item')];
    this.controls = this.querySelector('[data-category-controls]');
    if (!this.track || !this.items.length || !this.controls) return;
    this.index = 0;
    this.dots = [...this.querySelectorAll('[data-category-index]')];
    this.previous = this.querySelector('[data-category-prev]');
    this.next = this.querySelector('[data-category-next]');
    this.controls.hidden = false;
    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    };
    this.onControl = (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-category-index')) this.go(Number(button.dataset.categoryIndex));
      if (button === this.previous) this.go(this.index - 1);
      if (button === this.next) this.go(this.index + 1);
    };
    this.onKey = (event) => {
      if (event.target !== this.track || !matchMedia('(max-width: 767px)').matches) return;
      const keys = {ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.items.length - 1};
      if (!(event.key in keys)) return;
      event.preventDefault();
      this.go(keys[event.key]);
    };
    this.track.addEventListener('scroll', this.onScroll, {passive: true});
    this.controls.addEventListener('click', this.onControl);
    this.track.addEventListener('keydown', this.onKey);
    this.resize = new ResizeObserver(this.onScroll);
    this.resize.observe(this.track);
    this.update();
  }
  go(index) {
    index = Math.max(0, Math.min(this.items.length - 1, index));
    const left = this.items[index].offsetLeft - this.items[0].offsetLeft;
    this.track.scrollTo({left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
  }
  update() {
    const left = this.track.scrollLeft;
    this.index = this.items.reduce((nearest, item, index) => Math.abs(item.offsetLeft - this.items[0].offsetLeft - left) < Math.abs(this.items[nearest].offsetLeft - this.items[0].offsetLeft - left) ? index : nearest, 0);
    this.dots.forEach((dot, index) => dot.setAttribute('aria-pressed', String(index === this.index)));
    this.previous.disabled = this.index === 0;
    this.next.disabled = this.index === this.items.length - 1;
  }
  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('keydown', this.onKey);
    this.controls?.removeEventListener('click', this.onControl);
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('focus-category-carousel')) customElements.define('focus-category-carousel', FocusCategoryCarousel);

class FocusPickupSummary extends HTMLElement {
  connectedCallback() {
    this.source = document.querySelector('.focus-fulfillment [data-surface-pick-up]');
    if (!this.source) return;
    this.update = () => {
      const available = this.source.querySelector('.surface-pick-up-embed--available');
      this.replaceChildren();
      this.hidden = !available;
      if (!available) return;
      const title = available.querySelector('.surface-pick-up-embed__location-availability');
      const time = available.querySelector('.surface-pick-up-embed__location-pick-up-time');
      const original = available.querySelector('[data-surface-pick-up-embed-modal-btn]');
      if (title) { const p = document.createElement('p'); p.textContent = title.textContent; this.append(p); }
      if (time) { const p = document.createElement('p'); p.textContent = time.textContent; this.append(p); }
      if (original) { const button = document.createElement('button'); button.type = 'button'; button.textContent = original.textContent; button.setAttribute('aria-haspopup', 'dialog'); button.addEventListener('click', () => original.click()); this.append(button); }
    };
    this.observer = new MutationObserver(this.update);
    this.observer.observe(this.source, {childList: true, subtree: true, characterData: true, attributes: true});
    this.update();
  }
  disconnectedCallback() { this.observer?.disconnect(); }
}
if (!customElements.get('focus-pickup-summary')) customElements.define('focus-pickup-summary', FocusPickupSummary);
